// Коробка: корпус (скругления углов и кромок, отдельная толщина каждой стенки и дна), перегородки сеткой,
// кришка-ковпачок поверх стінок (з вікном або без). Корпус і кришка — два об'єкти: у 3MF окремо, у ZIP двома STL.
import { bedPicker, bedWarn, createStage, fitsBed, scope, solid, toGeometry, type Ctx, type Model } from '@gen3d/core';
import type { Manifold, ManifoldToplevel } from 'manifold-3d/manifold';
import { FACETED, PATTERNS, maxDepth, relief, thumb, type Face, type Pattern } from './texture';

type NumKey =
  | 'L' | 'W' | 'H' | 'R' | 'B' | 'T' | 'WL' | 'WR' | 'WF' | 'WB' | 'BR'
  | 'ROWS' | 'COLS' | 'TX' | 'TY' | 'DH'
  | 'LH' | 'LT' | 'C' | 'WM' | 'WRAD'
  | 'TP' | 'TD' | 'TF';
type S = Record<NumKey, number> & {
  edge: 'flat' | 'round'; bottom: 'none' | 'out' | 'in'; same: boolean; lid: boolean; win: boolean;
  tex: 'none' | Pattern; texLid: boolean;
};

const T = {
  uk: {
    size: 'Розмір', L: 'Довжина', W: 'Ширина', H: 'Висота', R: 'Радіус кутів',
    preset: 'Готовий варіант', own: 'Свої розміри',
    presets: ['Подарункова', 'Скринька', 'Для чаю', 'Органайзер'],
    edge: 'Верхня кромка', flat: 'Рівна', round: 'Заокруглена',
    walls: 'Стінки та дно', B: 'Товщина дна', same: 'Усі стінки однакові', T: 'Товщина стінок',
    WL: 'Ліва стінка', WR: 'Права стінка', WF: 'Передня стінка', WB: 'Задня стінка',
    bottom: 'Скруглення низу', none: 'Немає', out: 'Зовні', in: 'Всередині', BR: 'Радіус скруглення',
    hintOut: 'Заокруглений нижній край коробки.', hintIn: 'Плавний перехід від дна до стінок — легше діставати дрібниці.',
    div: 'Перегородки', ROWS: 'Ряди', COLS: 'Стовпці', TX: 'Товщина поперечних', TY: 'Товщина поздовжніх', DH: 'Висота перегородок',
    cell: 'Комірка', lidG: 'Кришка', lid: 'Кришка', LH: 'Висота кришки', LT: 'Товщина кришки', C: 'Зазор', fitTight: 'Щільно', fitFree: 'Вільно',
    winOn: 'Вікно в кришці', WM: 'Відступ вікна від краю', WRAD: 'Радіус кутів вікна',
    lidInfo: 'Кришка {s} мм надівається на стінки. Друкується поруч, догори дном.',
    fmt: { '3MF': 'корпус і кришка', ZIP: 'два STL' } as Record<string, string>, fmtOne: { '3MF': 'лише корпус', ZIP: 'лише корпус' } as Record<string, string>,
    inner: 'Всередині', mm: 'мм', pcs: 'шт', min: 'мін.', max: 'макс.', ok: 'Коробка вміщується', loading: 'Будую модель…', err: 'Не вдалося побудувати модель',
    texG: 'Фактура', pat: { none: 'Без фактури', knurl: 'Накатка', hex: 'Соти', ribs: 'Ребра', wave: 'Хвиля', pyr: 'Пірамідки', dots: 'Крапки' } as Record<string, string>,
    TP: 'Розмір візерунка', TD: 'Глибина рельєфу', TF: 'Рамка', texLid: 'Візерунок на кришці',
    texInfo: 'Рельєф виступає назовні, всередині коробка гладка. Під кришкою стінка гладка — візерунок продовжується на кришці.',
    texInfoNoLid: 'Рельєф виступає назовні, всередині коробка гладка.',
    texClamp: 'Глибину зменшено до {d} мм: глибший рельєф друкувався б із нависаннями.',
  },
  ru: {
    size: 'Размер', L: 'Длина', W: 'Ширина', H: 'Высота', R: 'Радиус углов',
    preset: 'Готовый вариант', own: 'Свои размеры',
    presets: ['Подарочная', 'Шкатулка', 'Для чая', 'Органайзер'],
    edge: 'Верхняя кромка', flat: 'Ровная', round: 'Скруглённая',
    walls: 'Стенки и дно', B: 'Толщина дна', same: 'Все стенки одинаковые', T: 'Толщина стенок',
    WL: 'Левая стенка', WR: 'Правая стенка', WF: 'Передняя стенка', WB: 'Задняя стенка',
    bottom: 'Скругление низа', none: 'Нет', out: 'Снаружи', in: 'Внутри', BR: 'Радиус скругления',
    hintOut: 'Скруглённый нижний край коробки.', hintIn: 'Плавный переход от дна к стенкам — мелочь легче доставать.',
    div: 'Перегородки', ROWS: 'Ряды', COLS: 'Столбцы', TX: 'Толщина поперечных', TY: 'Толщина продольных', DH: 'Высота перегородок',
    cell: 'Ячейка', lidG: 'Крышка', lid: 'Крышка', LH: 'Высота крышки', LT: 'Толщина крышки', C: 'Зазор', fitTight: 'Плотно', fitFree: 'Свободно',
    winOn: 'Окно в крышке', WM: 'Отступ окна от края', WRAD: 'Радиус углов окна',
    lidInfo: 'Крышка {s} мм надевается на стенки. Печатается рядом, вверх дном.',
    fmt: { '3MF': 'корпус и крышка', ZIP: 'два STL' } as Record<string, string>, fmtOne: { '3MF': 'только корпус', ZIP: 'только корпус' } as Record<string, string>,
    inner: 'Внутри', mm: 'мм', pcs: 'шт', min: 'мин.', max: 'макс.', ok: 'Коробка помещается', loading: 'Строю модель…', err: 'Не удалось построить модель',
    texG: 'Фактура', pat: { none: 'Без фактуры', knurl: 'Накатка', hex: 'Соты', ribs: 'Рёбра', wave: 'Волна', pyr: 'Пирамидки', dots: 'Точки' } as Record<string, string>,
    TP: 'Размер узора', TD: 'Глубина рельефа', TF: 'Рамка', texLid: 'Узор на крышке',
    texInfo: 'Рельеф выступает наружу, внутри коробка гладкая. Под крышкой стенка гладкая — узор продолжается на крышке.',
    texInfoNoLid: 'Рельеф выступает наружу, внутри коробка гладкая.',
    texClamp: 'Глубина уменьшена до {d} мм: более глубокий рельеф печатался бы с нависаниями.',
  },
  en: {
    size: 'Size', L: 'Length', W: 'Width', H: 'Height', R: 'Corner radius',
    preset: 'Preset', own: 'Custom size',
    presets: ['Gift box', 'Keepsake', 'Tea box', 'Desk organizer'],
    edge: 'Top edge', flat: 'Flat', round: 'Rounded',
    walls: 'Walls and floor', B: 'Floor thickness', same: 'All walls the same', T: 'Wall thickness',
    WL: 'Left wall', WR: 'Right wall', WF: 'Front wall', WB: 'Back wall',
    bottom: 'Bottom rounding', none: 'None', out: 'Outside', in: 'Inside', BR: 'Rounding radius',
    hintOut: 'Rounded bottom edge of the box.', hintIn: 'Smooth floor-to-wall transition — small items are easier to scoop out.',
    div: 'Dividers', ROWS: 'Rows', COLS: 'Columns', TX: 'Cross divider thickness', TY: 'Long divider thickness', DH: 'Divider height',
    cell: 'Cell', lidG: 'Lid', lid: 'Lid', LH: 'Lid height', LT: 'Lid thickness', C: 'Clearance', fitTight: 'Snug', fitFree: 'Loose',
    winOn: 'Window in the lid', WM: 'Window inset from edge', WRAD: 'Window corner radius',
    lidInfo: 'The {s} mm lid slides over the walls. Printed next to the box, upside down.',
    fmt: { '3MF': 'box and lid', ZIP: 'two STLs' } as Record<string, string>, fmtOne: { '3MF': 'box only', ZIP: 'box only' } as Record<string, string>,
    inner: 'Inside', mm: 'mm', pcs: 'pcs', min: 'min', max: 'max', ok: 'Box fits', loading: 'Building model…', err: 'Could not build the model',
    texG: 'Texture', pat: { none: 'None', knurl: 'Knurling', hex: 'Honeycomb', ribs: 'Ribs', wave: 'Wave', pyr: 'Pyramids', dots: 'Dots' } as Record<string, string>,
    TP: 'Pattern size', TD: 'Relief depth', TF: 'Border', texLid: 'Pattern on the lid',
    texInfo: 'The relief sticks out; the inside stays smooth. The wall under the lid is smooth — the pattern continues on the lid.',
    texInfoNoLid: 'The relief sticks out; the inside stays smooth.',
    texClamp: 'Depth reduced to {d} mm: deeper relief would print with overhangs.',
  },
};

// [мин, макс, шаг, по умолчанию]
const LIM: Record<NumKey, [number, number, number, number]> = {
  L: [20, 400, 0.1, 120], W: [20, 400, 0.1, 80], H: [5, 400, 0.1, 45], R: [0, 40, 0.5, 6],
  B: [0.8, 15, 0.1, 2.4], T: [0.8, 30, 0.1, 2.4], WL: [0.8, 30, 0.1, 2.4], WR: [0.8, 30, 0.1, 2.4], WF: [0.8, 30, 0.1, 2.4], WB: [0.8, 30, 0.1, 2.4],
  BR: [0.2, 10, 0.1, 1.6],
  ROWS: [1, 16, 1, 1], COLS: [1, 16, 1, 1], TX: [0.6, 8, 0.1, 1.2], TY: [0.6, 8, 0.1, 1.2], DH: [2, 400, 0.1, 45],
  LH: [4, 40, 0.1, 10], LT: [0.8, 10, 0.1, 2.4], C: [0.1, 2, 0.01, 0.35], WM: [2, 60, 0.5, 10], WRAD: [0, 40, 0.5, 4],
  TP: [2, 30, 0.5, 6], TD: [0.3, 2, 0.1, 0.8], TF: [1, 20, 0.5, 3],
};
const PCS = new Set<NumKey>(['ROWS', 'COLS']);
const FIT = { tight: 0.12, free: 0.35 };
/** Готові варіанти: розміри, радіус, сітка перегородок, кришка. Решту (стінки, дно) не чіпаємо. */
const PRESETS: { set: Partial<Record<NumKey, number>>; lid: boolean }[] = [
  { set: { L: 120, W: 80, H: 45, R: 8, ROWS: 1, COLS: 1 }, lid: true },
  { set: { L: 100, W: 100, H: 35, R: 14, ROWS: 1, COLS: 1 }, lid: true },
  { set: { L: 220, W: 90, H: 70, R: 6, ROWS: 1, COLS: 4, DH: 55 }, lid: true },
  { set: { L: 160, W: 100, H: 35, R: 6, ROWS: 2, COLS: 3, DH: 28 }, lid: false },
];
const GAP = 10; // між корпусом і кришкою на столі
const SEG = 12; // відрізків на чверть кола

const clamp = (v: number, [a, b]: readonly number[]) => Math.min(b, Math.max(a, v));
const r2 = (v: number) => Math.round(v * 100) / 100;
const fmt = (v: number) => String(r2(v));

/**
 * Опукле тіло: прямокутник зі скругленими кутами (центр cx,cy, розмір w×h, радіус r), «поверхами».
 * levels — [висота, відступ контуру всередину] знизу вгору; від'ємний відступ — контур ширший.
 * Так будуються скруглені кромки без булевих операцій: кожен поверх — той самий контур, зсунутий offset'ом.
 */
function slab(M: ManifoldToplevel, cx: number, cy: number, w: number, h: number, r: number, levels: [number, number][]): Manifold {
  const lv = levels.filter((l, i) => i === 0 || l[0] > levels[i - 1][0] + 1e-6);
  const P = 4 * (SEG + 1);
  const pos: number[] = [];
  for (const [z, d] of lv) {
    const ww = w / 2 - d, hh = h / 2 - d;
    const rr = Math.max(0.001, Math.min(r - d, ww - 0.001, hh - 0.001));
    const qs: [number, number, number][] = [[ww - rr, hh - rr, 0], [-ww + rr, hh - rr, 1], [-ww + rr, -hh + rr, 2], [ww - rr, -hh + rr, 3]];
    for (const [x, y, q] of qs)
      for (let i = 0; i <= SEG; i++) {
        const a = (q + i / SEG) * Math.PI / 2;
        pos.push(cx + x + rr * Math.cos(a), cy + y + rr * Math.sin(a), z);
      }
  }
  const n = lv.length;
  const c0 = pos.length / 3, c1 = c0 + 1;
  pos.push(cx, cy, lv[0][0], cx, cy, lv[n - 1][0]);
  const tri: number[] = [];
  for (let i = 0; i < P; i++) {
    const j = (i + 1) % P;
    tri.push(c0, j, i); // дно
    tri.push(c1, (n - 1) * P + i, (n - 1) * P + j); // верх
    for (let k = 0; k < n - 1; k++) {
      const a0 = k * P + i, a1 = k * P + j, b0 = a0 + P, b1 = a1 + P;
      tri.push(a0, a1, b1, a0, b1, b0);
    }
  }
  return new M.Manifold(new M.Mesh({ numProp: 3, vertProperties: new Float32Array(pos), triVerts: new Uint32Array(tri) }));
}

/** Чверть кола: [висота, відступ] від z0 вгору на r. up — кромка вгорі (відступ росте), інакше внизу (відступ спадає). */
function quarter(z0: number, r: number, up: boolean, sign = 1): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= SEG; i++) {
    const a = (i / SEG) * Math.PI / 2;
    out.push(up ? [z0 + r * Math.sin(a), sign * (r - r * Math.cos(a))] : [z0 + r - r * Math.cos(a), sign * (r - r * Math.sin(a))]);
  }
  return out;
}

/** Сховище сторінки (параметри в межах вкладки), якщо сторінка його дає. */
type Store = { load(): unknown; save(v: unknown): void };

export function mount(root: HTMLElement, { lang, store }: Ctx & { store?: Store }) {
  const t = T[lang] ?? T.uk;
  const s = { edge: 'flat', bottom: 'none', same: true, lid: true, win: false, tex: 'none', texLid: true } as S;
  for (const k of Object.keys(LIM) as NumKey[]) s[k] = LIM[k][3];
  try {
    const v = (store?.load() as { s?: Record<string, any> } | null)?.s;
    if (v && typeof v === 'object') {
      for (const k of Object.keys(LIM) as NumKey[]) if (typeof v[k] === 'number' && isFinite(v[k])) s[k] = clamp(v[k], LIM[k]);
      if (v.edge === 'flat' || v.edge === 'round') s.edge = v.edge;
      if (['none', 'out', 'in'].includes(v.bottom)) s.bottom = v.bottom;
      for (const b of ['same', 'lid', 'win', 'texLid'] as const) if (typeof v[b] === 'boolean') s[b] = v[b];
      if (v.tex === 'none' || PATTERNS.includes(v.tex)) s.tex = v.tex;
    }
  } catch { /* типові */ }

  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const panel = root.querySelector<HTMLElement>('[data-params]')!;
  const viewer = createStage(stage, lang).viewer;

  // ---------- панель ----------
  const rng = (k: NumKey) => {
    const [min, max, step] = LIM[k];
    const label = t[k];
    return `<div class="rng" data-k="${k}"><span class="lbl">${label}</span><span class="rng-row">` +
      `<input type="range" min="${min}" max="${max}" step="${step}" value="${s[k]}" aria-label="${label}">` +
      `<span class="num"><input type="number" min="${min}" max="${max}" step="${step}" value="${s[k]}" inputmode="decimal" aria-label="${label}"><i>${PCS.has(k) ? t.pcs : t.mm}</i></span></span>` +
      `<span class="minmax">${t.min} ${min} · ${t.max} ${max}</span></div>`;
  };
  const seg = (k: string, opts: [string, string][], cur: string) =>
    `<div class="seg wide" data-seg="${k}">${opts.map(([v, l]) => `<button type="button" data-v="${v}" class="${v === cur ? 'on' : ''}">${l}</button>`).join('')}</div>`;
  const sw = (k: string, label: string, on: boolean) => `<label class="switch"><input type="checkbox" data-sw="${k}"${on ? ' checked' : ''}><span>${label}</span></label>`;

  panel.innerHTML =
    `<details class="group" open><summary>${t.size}</summary>` +
      `<label class="field flat"><span class="lbl">${t.preset}</span><select data-presets><option value="">${t.own}</option>` +
        PRESETS.map((p, i) => `<option value="${i}">${t.presets[i]} ${p.set.L}×${p.set.W}×${p.set.H}</option>`).join('') + '</select></label>' +
      rng('L') + rng('W') + rng('H') + rng('R') +
      `<span class="lbl gap">${t.edge}</span>` + seg('edge', [['flat', t.flat], ['round', t.round]], s.edge) +
      '<p class="hint calc" data-inner></p></details>' +
    `<details class="group" open><summary>${t.walls}</summary>` +
      rng('B') + sw('same', t.same, s.same) +
      `<div data-one>${rng('T')}</div><div data-four>${rng('WL')}${rng('WR')}${rng('WF')}${rng('WB')}</div>` +
      `<span class="lbl gap">${t.bottom}</span>` + seg('bottom', [['none', t.none], ['out', t.out], ['in', t.in]], s.bottom) +
      `<div data-br>${rng('BR')}</div><p class="hint" data-hint-bottom></p></details>` +
    `<details class="group"><summary>${t.div}</summary>` +
      rng('ROWS') + rng('COLS') + `<div data-div-opts>${rng('TX')}${rng('TY')}${rng('DH')}</div><p class="hint calc" data-cell></p></details>` +
    `<details class="group"><summary>${t.lidG}</summary>` +
      sw('lid', t.lid, s.lid) +
      `<div data-lid-opts>${rng('LH')}${rng('LT')}` +
        `<div class="chips" data-fit-chips><button type="button" class="chip" data-c="${FIT.tight}">${t.fitTight} ${FIT.tight}</button><button type="button" class="chip" data-c="${FIT.free}">${t.fitFree} ${FIT.free}</button></div>` +
        rng('C') + sw('win', t.winOn, s.win) + `<div data-win-opts>${rng('WM')}${rng('WRAD')}</div>` +
        '<p class="hint calc" data-lid-info></p></div></details>' +
    `<details class="group"${s.tex !== 'none' ? ' open' : ''}><summary>${t.texG}</summary>` +
      `<div class="bx-tex" data-tex>${(['none', ...PATTERNS] as const).map((x) =>
        `<button type="button" data-t="${x}" class="${x === s.tex ? 'on' : ''}" aria-pressed="${x === s.tex}">` +
        (x === 'none' ? '<i></i>' : `<img alt="" src="${thumb(x)}">`) + `<span>${t.pat[x]}</span></button>`).join('')}</div>` +
      `<div data-tex-opts>${rng('TP')}${rng('TD')}${rng('TF')}<div data-tex-lid>${sw('texLid', t.texLid, s.texLid)}</div>` +
        '<p class="hint calc" data-tex-clamp></p><p class="hint" data-tex-info></p></div></details>' +
    '<style>.bx-tex{display:grid;grid-template-columns:repeat(auto-fill,minmax(74px,1fr));gap:6px;margin:4px 0 8px}' +
      '.bx-tex button{display:flex;flex-direction:column;align-items:center;gap:4px;min-height:44px;padding:4px 2px 6px;border:1px solid var(--line);border-radius:var(--radius-sm);background:var(--surface);color:var(--muted);font:inherit;font-size:12px;line-height:1.2;cursor:pointer}' +
      '.bx-tex button.on{border-color:var(--text);color:var(--text);box-shadow:inset 0 0 0 1px var(--text)}' +
      '.bx-tex img,.bx-tex i{width:100%;max-width:56px;aspect-ratio:1;border-radius:calc(var(--radius-sm) - 2px);display:block}' +
      '.bx-tex i{background:var(--surface-2,var(--line))}</style>';

  const $ = <E extends Element = HTMLElement>(q: string) => panel.querySelector<E>(q)!;
  const setNum = (k: NumKey, v: number) => {
    s[k] = clamp(v, LIM[k]);
    panel.querySelectorAll<HTMLInputElement>(`[data-k="${k}"] input`).forEach((i) => (i.value = String(s[k])));
  };

  panel.querySelectorAll<HTMLElement>('.rng').forEach((el) => {
    const k = el.dataset.k as NumKey;
    const [r, n] = el.querySelectorAll('input');
    const set = (raw: string) => {
      // пустое или не число — прежнее значение
      const x = raw.trim() === '' ? NaN : +raw;
      setNum(k, Number.isFinite(x) ? x : s[k]);
      // одна товщина для всіх стінок — тримаємо чотири поля в лад
      if (k === 'T') for (const w of ['WL', 'WR', 'WF', 'WB'] as const) setNum(w, s.T);
      if (['L', 'W', 'H'].includes(k)) reframe = true;
      sync(); rebuild();
    };
    r.oninput = () => set(r.value);
    n.onchange = () => set(n.value);
  });
  $<HTMLSelectElement>('[data-presets]').onchange = (e) => {
    const v = (e.target as HTMLSelectElement).value;
    if (v === '') return;
    const p = PRESETS[+v];
    for (const [k, v] of Object.entries(p.set)) setNum(k as NumKey, v);
    if (!('DH' in p.set)) setNum('DH', s.H);
    s.lid = p.lid; $<HTMLInputElement>('[data-sw="lid"]').checked = p.lid;
    reframe = true; sync(); rebuild();
  };
  $('[data-tex]').onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-t]');
    if (!b) return;
    const was = s.tex;
    s.tex = b.dataset.t as S['tex'];
    $('[data-tex]').querySelectorAll<HTMLElement>('[data-t]').forEach((x) => { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', String(x === b)); });
    sync(); rebuild();
    if (was === 'none' && s.tex !== 'none') reveal($('[data-tex-opts]'));
  };
  $('[data-fit-chips]').onclick = (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-c]');
    if (b) { setNum('C', +b.dataset.c!); sync(); rebuild(); }
  };
  panel.querySelectorAll<HTMLElement>('[data-seg]').forEach((el) => {
    el.onclick = (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-v]');
      if (!b) return;
      el.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
      (s as any)[el.dataset.seg!] = b.dataset.v;
      sync(); rebuild();
    };
  });
  panel.querySelectorAll<HTMLInputElement>('[data-sw]').forEach((el) => {
    el.onchange = () => {
      const k = el.dataset.sw as 'same' | 'lid' | 'win' | 'texLid';
      s[k] = el.checked;
      if (k === 'same' && s.same) for (const w of ['WL', 'WR', 'WF', 'WB'] as const) setNum(w, s.T);
      if (k === 'lid') reframe = true;
      sync(); rebuild();
      const open = { same: s.same ? null : '[data-four]', lid: s.lid ? '[data-lid-opts]' : null, win: s.win ? '[data-win-opts]' : null, texLid: null }[k];
      if (open) reveal($(open));
    };
  });
  /** Прокрутити до блоку, що щойно відкрився, — щоб не ховався під кнопками завантаження. */
  const reveal = (el: HTMLElement) => {
    const bar = document.querySelector<HTMLElement>('.actions');
    el.style.scrollMarginBottom = `${(bar?.offsetHeight ?? 0) + 12}px`;
    el.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };
  let bed = bedPicker(panel, lang, (b) => { bed = b; viewer.setBed(b.x, b.y); reframe = true; rebuild(); });
  viewer.setBed(bed.x, bed.y);

  // ---------- розміри, що залежать один від одного ----------
  /** Фактичні величини: обмеження, які залежать від інших полів, застосовуються тут, а не в повзунках. */
  function eff(p: S) {
    const wl = p.same ? p.T : p.WL, wr = p.same ? p.T : p.WR, wf = p.same ? p.T : p.WF, wb = p.same ? p.T : p.WB;
    // внутрішня порожнина не менша за 2 мм
    const sx = Math.min(1, (p.L - 2) / (wl + wr)), sy = Math.min(1, (p.W - 2) / (wf + wb));
    const w = { l: wl * sx, r: wr * sx, f: wf * sy, b: wb * sy };
    const B = Math.min(p.B, p.H - 1);
    const iL = p.L - w.l - w.r, iW = p.W - w.f - w.b, iH = p.H - B;
    const R = Math.min(p.R, p.L / 2, p.W / 2);
    const minW = Math.min(w.l, w.r, w.f, w.b);
    const rt = p.edge === 'round' ? Math.min(minW / 2 * 0.96, iH / 2) : 0;
    const ro = p.bottom === 'out' ? Math.min(p.BR, B * 0.95, minW * 0.95, (p.H - rt) * 0.95) : 0;
    const ri = p.bottom === 'in' ? Math.min(p.BR, iL / 2 * 0.95, iW / 2 * 0.95, (iH - rt) * 0.95) : 0;
    const DH = Math.min(p.DH, p.H);
    const cols = p.COLS, rows = p.ROWS;
    const cellL = (iL - (cols - 1) * p.TX) / cols, cellW = (iW - (rows - 1) * p.TY) / rows;
    const C = p.C, LT = p.LT, LH = Math.max(p.LH, LT + 1);
    const lidL = p.L + 2 * (C + LT), lidW = p.W + 2 * (C + LT);
    // фактура: глибина не більша, ніж дозволяє візерунок без нависань
    const TD = p.tex === 'none' ? 0 : Math.min(p.TD, maxDepth(p.tex, p.TP));
    return { w, B, iL, iW, iH, R, rt, ro, ri, DH, rows, cols, cellL, cellW, C, LT, LH, lidL, lidW, TD };
  }

  /**
   * Корпус і кришка поруч, разом — по центру столу. Кришка праворуч, а якщо так не влазить у стіл,
   * а позаду влазить — позаду. Не влазить ніяк — праворуч (слайсер рознесе по столах).
   */
  function layout(p: S, e: ReturnType<typeof eff>) {
    if (!p.lid) return { bx: 0, by: 0, lx: 0, ly: 0, sx: p.L, sy: p.W };
    const byX = { sx: p.L + GAP + e.lidL, sy: Math.max(p.W, e.lidW) };
    const byY = { sx: Math.max(p.L, e.lidL), sy: p.W + GAP + e.lidW };
    const alongY = byX.sx > bed.x && byY.sx <= bed.x && byY.sy <= bed.y;
    return alongY
      ? { bx: 0, by: -byY.sy / 2 + p.W / 2, lx: 0, ly: byY.sy / 2 - e.lidW / 2, ...byY }
      : { bx: -byX.sx / 2 + p.L / 2, by: 0, lx: byX.sx / 2 - e.lidL / 2, ly: 0, ...byX };
  }

  /** Подписи и видимость, зависящие от выбора. */
  function sync() {
    const e = eff(s);
    // пресет у списку — поки параметри збігаються з ним; змінили щось — «Свої розміри»
    const pi = PRESETS.findIndex((x) => x.lid === s.lid && Object.entries(x.set).every(([k, v]) => s[k as NumKey] === v));
    $<HTMLSelectElement>('[data-presets]').value = pi < 0 ? '' : String(pi);
    $('[data-one]').hidden = !s.same;
    $('[data-four]').hidden = s.same;
    $('[data-br]').hidden = s.bottom === 'none';
    $('[data-hint-bottom]').textContent = s.bottom === 'out' ? t.hintOut : s.bottom === 'in' ? t.hintIn : '';
    $('[data-hint-bottom]').hidden = s.bottom === 'none';
    $('[data-div-opts]').hidden = s.ROWS === 1 && s.COLS === 1;
    $('[data-lid-opts]').hidden = !s.lid;
    fmtNotes();
    $('[data-win-opts]').hidden = !s.win;
    $('[data-inner]').textContent = `${t.inner}: ${fmt(e.iL)} × ${fmt(e.iW)} × ${fmt(e.iH)} ${t.mm}`;
    const cell = $('[data-cell]');
    cell.hidden = s.ROWS === 1 && s.COLS === 1;
    cell.textContent = `${t.cell}: ${fmt(e.cellL)} × ${fmt(e.cellW)} ${t.mm}`;
    $('[data-lid-info]').textContent = t.lidInfo.replace('{s}', `${fmt(e.lidL)} × ${fmt(e.lidW)} × ${fmt(e.LH)}`);
    $('[data-tex-opts]').hidden = s.tex === 'none';
    $('[data-tex-lid]').hidden = !s.lid;
    $('[data-tex-info]').textContent = s.lid ? t.texInfo : t.texInfoNoLid;
    const cl = $('[data-tex-clamp]');
    cl.hidden = s.tex === 'none' || e.TD >= s.TD - 1e-9;
    cl.textContent = t.texClamp.replace('{d}', fmt(Math.floor(e.TD * 10) / 10));
  }

  // ---------- геометрия ----------
  const dims = stage.querySelector<HTMLElement>('[data-dims]')!;
  const fit = stage.querySelector<HTMLElement>('[data-fit]')!;
  let current: Promise<Model> | null = null;
  const setReady = (on: boolean) => root.querySelectorAll<HTMLButtonElement>('[data-download]').forEach((b) => { b.disabled = !on; });
  // общая подпись страницы («2 кольори») коробке не подходит: тут корпус і кришка, а не два кольори
  const fmtNotes = () => root.querySelectorAll<HTMLElement>('[data-download] small').forEach((el) => {
    const f = el.closest<HTMLElement>('[data-download]')!.dataset.download!, txt = (s.lid ? t.fmt : t.fmtOne)[f];
    if (txt) el.textContent = txt;
  });
  let seq = 0, timer = 0, reframe = true;

  function rebuild() {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const my = ++seq;
      const p = { ...s };
      try { store?.save({ s }); } catch { /* сховище недоступне */ }
      const job = build(p);
      current = job;
      job.then((m) => {
        if (my !== seq) return;
        viewer.setParts(m.parts);
        const e = eff(p);
        const ok = fitsBed(bed, p.L, p.W, p.H) && (!p.lid || fitsBed(bed, e.lidL, e.lidW, e.LH));
        fit.className = ok ? 'fit' : 'fit bad';
        fit.textContent = ok ? t.ok : bedWarn(lang, bed);
        setReady(true);
        if (reframe) { const l = layout(p, e); viewer.frame(l.sx, l.sy, p.H); reframe = false; }
        dims.innerHTML = `<b>${fmt(p.L)}</b> × <b>${fmt(p.W)}</b> × <b>${fmt(p.H)}</b> ${t.mm}`;
      }, (err) => {
        console.error(err);
        if (my === seq) { fit.className = 'fit bad'; fit.textContent = t.err; setReady(false); }
      });
    }, 60);
  }

  async function build(p: S): Promise<Model> {
    const M = await solid();
    const k = scope();
    try {
      const e = eff(p);
      const { L, W, H } = p;
      const { bx, by, lx, ly } = layout(p, e);

      // корпус: зовнішнє тіло мінус порожнина
      const outerLv: [number, number][] = [
        ...(e.ro ? quarter(0, e.ro, false) : [[0, 0] as [number, number]]),
        ...(e.rt ? quarter(H - e.rt, e.rt, true) : [[H, 0] as [number, number]]),
      ];
      const outer = k(slab(M, bx, by, L, W, e.R, outerLv));
      const icx = bx + (e.w.l - e.w.r) / 2, icy = by + (e.w.f - e.w.b) / 2;
      const iR = Math.max(0, e.R - Math.min(e.w.l, e.w.r, e.w.f, e.w.b));
      const cavLv: [number, number][] = [
        ...(e.ri ? quarter(e.B, e.ri, false) : [[e.B, 0] as [number, number]]),
        ...(e.rt ? quarter(H - e.rt, e.rt, true, -1) : [[H, 0] as [number, number]]),
        [H + 1, -e.rt],
      ];
      let body = k(outer.subtract(k(slab(M, icx, icy, e.iL, e.iW, iR, cavLv))));

      // перегородки: рівні комірки; за межі корпусу не виходять
      if (e.rows > 1 || e.cols > 1) {
        const walls: Manifold[] = [];
        const x0 = icx - e.iL / 2, y0 = icy - e.iW / 2, z0 = e.B - 0.01, h = e.DH - z0;
        for (let i = 1; i < e.cols; i++)
          walls.push(k(k(M.Manifold.cube([p.TX, e.iW + 2, h])).translate([x0 + i * (e.cellL + p.TX) - p.TX, y0 - 1, z0])));
        for (let j = 1; j < e.rows; j++)
          walls.push(k(k(M.Manifold.cube([e.iL + 2, p.TY, h])).translate([x0 - 1, y0 + j * (e.cellW + p.TY) - p.TY, z0])));
        if (h > 0 && walls.length) body = k(body.add(k(k(M.Manifold.union(walls)).intersect(outer))));
      }
      // фактура: панелі на плоских частинах стінок; під кришкою стінка гладка, візерунок іде далі на кришці
      const tex = p.tex === 'none' ? null : p.tex;
      const F = p.TF, seam = p.lid ? H + e.LT - e.LH : 0;
      const hwX = L / 2 - e.R - F, hwY = W / 2 - e.R - F;
      const textured = (m: Manifold, fs: Face[]) => {
        const r = fs.filter((f) => f.hw >= 1 && f.z1 - f.z0 >= 1).map((f) => k(relief(M, f, tex!, p.TP, e.TD)));
        if (!r.length) return m;
        const u = k(m.add(k(M.Manifold.union(r))));
        return FACETED.has(tex!) ? k(u.simplify(0.005)) : u;
      };
      if (tex) {
        const z0 = Math.max(F, e.ro), z1 = p.lid ? seam : H - Math.max(F, e.rt);
        const f = (cx: number, cy: number, nx: number, ny: number, hw: number, wall: number): Face =>
          ({ cx, cy, nx, ny, hw, z0, z1, sv: 1, cv: -seam, inset: Math.min(0.4, wall / 2) });
        body = textured(body, [
          f(bx, by - W / 2, 0, -1, hwX, e.w.f), f(bx, by + W / 2, 0, 1, hwX, e.w.b),
          f(bx - L / 2, by, -1, 0, hwY, e.w.l), f(bx + L / 2, by, 1, 0, hwY, e.w.r),
        ]);
      }
      const parts: Model['parts'] = [{ name: 'box', role: 'base', geometry: toGeometry(body) }];

      // кришка-ковпачок: дном на столі, бортом догори
      if (p.lid) {
        const lr = e.R + e.C + e.LT;
        const lro = p.edge === 'round' ? Math.min(e.LT * 0.9, e.LH / 2) : 0;
        const lidOuter = k(slab(M, lx, ly, e.lidL, e.lidW, lr, [
          ...(lro ? quarter(0, lro, false) : [[0, 0] as [number, number]]), [e.LH, 0]]));
        let lid = k(lidOuter.subtract(k(slab(M, lx, ly, L + 2 * e.C, W + 2 * e.C, e.R + e.C, [[e.LT, 0], [e.LH + 1, 0]]))));
        if (p.win) {
          const ww = L + 2 * e.C - 2 * p.WM, wh = W + 2 * e.C - 2 * p.WM;
          if (ww > 2 && wh > 2) lid = k(lid.subtract(k(slab(M, lx, ly, ww, wh, Math.min(p.WRAD, ww / 2, wh / 2), [[-1, 0], [e.LT + 1, 0]]))));
        }
        // кришка друкується догори дном: висота візерунку відраховується від краю борту (стику з коробкою)
        if (tex && p.texLid) {
          const z0 = Math.max(F, lro), z1 = e.LH;
          const f = (cx: number, cy: number, nx: number, ny: number, hw: number): Face =>
            ({ cx, cy, nx, ny, hw, z0, z1, sv: -1, cv: e.LH, inset: Math.min(0.4, e.LT / 2) });
          lid = textured(lid, [
            f(lx, ly - e.lidW / 2, 0, -1, hwX), f(lx, ly + e.lidW / 2, 0, 1, hwX),
            f(lx - e.lidL / 2, ly, -1, 0, hwY), f(lx + e.lidL / 2, ly, 1, 0, hwY),
          ]);
        }
        parts.push({ name: 'lid', role: 'base', geometry: toGeometry(lid) });
      }
      const name = `box-${fmt(L)}x${fmt(W)}x${fmt(H)}`;
      return {
        name, parts,
        objects: parts.map((x, i) => ({ name: x.name, parts: [i], at: [[0, 0, 0]] })),
      };
    } finally {
      k.free();
    }
  }

  sync();
  rebuild();
  return { model: () => current ?? build(s) };
}
